/**
 * Phase J.3.2 — Reusable notification bell rendered in TopNav for both
 * customer and admin shells. Polls the unread-count every 8s while the
 * tab is visible. The dropdown panel itself fetches the latest 20 ON
 * OPEN (we don't keep the list hot).
 */
import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { toast } from "sonner";

import { useUnreadCount, useNotificationsList } from "@/hooks/useNotifications";
import { getEventMeta } from "@/lib/notificationEvents";

function relTime(iso) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "";
    const diff = Math.max(0, Date.now() - then);
    const sec = Math.round(diff / 1000);
    if (sec < 45) return "just now";
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 36) return `${hr}h ago`;
    const day = Math.round(hr / 24);
    if (day < 14) return `${day}d ago`;
    const wk = Math.round(day / 7);
    if (wk < 12) return `${wk}w ago`;
    return iso.slice(0, 10);
}


function BellItem({ row, onSelect }) {
    const meta = getEventMeta(row.event_type);
    const Icon = meta.icon;
    const isRead = Boolean(row.read_at);
    return (
        <button
            type="button"
            data-testid={`notification-item-${row.id}`}
            onClick={() => onSelect(row)}
            className={
                "w-full text-left px-3 py-3 border-b border-slate-100 last:border-0 transition-colors " +
                (isRead
                    ? "bg-white hover:bg-slate-50"
                    : "bg-blue-50/40 hover:bg-blue-50/70")
            }
        >
            <div className="flex items-start gap-3">
                <span className={"mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full ring-1 ring-inset " + meta.accent}>
                    <Icon size={14} aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                        <p className={"text-sm truncate " + (isRead ? "text-slate-700 font-medium" : "text-slate-900 font-semibold")}>
                            {row.title}
                        </p>
                        {!isRead && (
                            <span
                                aria-hidden="true"
                                className="ml-auto inline-block h-1.5 w-1.5 rounded-full bg-blue-600 shrink-0"
                            />
                        )}
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{row.body}</p>
                    <p className="text-[11px] text-slate-400 mt-1">{relTime(row.created_at)}</p>
                </div>
            </div>
        </button>
    );
}


export function NotificationBell({ role }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const { count, refresh: refreshCount, setCount } = useUnreadCount({ role });
    const { items, loading, refresh, markRead, markAllRead } = useNotificationsList({
        role, read: "all", limit: 20,
    });

    // Refresh list on open. Don't poll while open — count poll still runs.
    useEffect(() => {
        if (open) {
            refresh();
        }
    }, [open, refresh]);

    // Click outside to close.
    useEffect(() => {
        function onDown(e) {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(e.target)) setOpen(false);
        }
        if (open) document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    const handleSelect = useCallback(async (row) => {
        // Mark read first so the count updates before we navigate.
        if (!row.read_at) {
            await markRead(row.id);
            setCount((n) => Math.max(0, n - 1));
        }
        setOpen(false);
        const cta = row.cta_url;
        if (!cta) return;
        if (cta.startsWith("http")) {
            window.location.assign(cta);
        } else {
            navigate(cta);
        }
    }, [markRead, navigate, setCount]);

    const handleMarkAll = useCallback(async () => {
        if (count === 0) return;
        await markAllRead();
        setCount(0);
        await refreshCount();
        toast.success("All notifications marked as read");
    }, [count, markAllRead, refreshCount, setCount]);

    const badgeText = count > 9 ? "9+" : String(count);

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="notification-bell-trigger"
                aria-label="Notifications"
                aria-haspopup="dialog"
                aria-expanded={open}
                className="relative inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
            >
                <Bell size={16} aria-hidden="true" />
                {count > 0 && (
                    <span
                        data-testid="notification-bell-badge"
                        className="absolute -top-1 -right-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-semibold text-white ring-2 ring-white"
                    >
                        {badgeText}
                    </span>
                )}
            </button>

            {open && (
                <div
                    data-testid="notification-bell-panel"
                    className="absolute right-0 mt-2 w-[360px] max-w-[calc(100vw-32px)] rounded-lg border border-slate-200 bg-white shadow-xl ring-1 ring-black/5 z-50"
                    role="dialog"
                    aria-label="Notifications panel"
                >
                    <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100">
                        <div className="text-sm font-semibold text-slate-900">Notifications</div>
                        <button
                            type="button"
                            data-testid="notification-bell-mark-all"
                            onClick={handleMarkAll}
                            disabled={count === 0}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:text-blue-900 disabled:text-slate-400 disabled:cursor-not-allowed"
                        >
                            <CheckCheck size={12} aria-hidden="true" />
                            Mark all as read
                        </button>
                    </div>
                    <div className="max-h-[420px] overflow-y-auto" data-testid="notification-bell-list">
                        {loading && items.length === 0 ? (
                            <div className="px-3 py-8 text-center text-sm text-slate-500">Loading…</div>
                        ) : items.length === 0 ? (
                            <div className="px-3 py-10 text-center">
                                <p className="text-sm text-slate-600">You&rsquo;re all caught up.</p>
                                <p className="text-xs text-slate-400 mt-1">New activity will show here.</p>
                            </div>
                        ) : (
                            items.map((row) => (
                                <BellItem key={row.id} row={row} onSelect={handleSelect} />
                            ))
                        )}
                    </div>
                    <div className="border-t border-slate-100 px-3 py-2 text-right">
                        <button
                            type="button"
                            data-testid="notification-bell-see-all"
                            onClick={() => {
                                setOpen(false);
                                navigate(`/${role === "customer" ? "customer" : "admin"}/notifications`);
                            }}
                            className="text-xs font-medium text-blue-700 hover:text-blue-900"
                        >
                            See all notifications &rarr;
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default NotificationBell;
